/**
 * ASSIST-01 — the Assist card on a listing page (waitlist variant).
 *
 * Renders nothing until `useAssistMode()` has resolved on the client, so the
 * server-rendered property page (CDN-cached, no user known) never contains
 * Assist markup (brief R2a.5). Then it renders only when:
 *   - the mode is not `off`, or the viewer is a tester (R2a; testers see the
 *     same waitlist card in ASSIST-01);
 *   - the listing is eligible (R17): native, published, a pilot type in a
 *     pilot city, and not the viewer's own listing (R9);
 *   - for a signed-in viewer, their waitlist status has loaded (so a joined
 *     user never sees the button flash first).
 *
 * A guest's tap hands the host a return path with `?assist=join` (R18.5); the
 * host stores it and shows its existing sign-in prompt. Back on the listing,
 * signed in, the first card to see the parameter removes it and registers,
 * through the idempotent RPC, exactly once.
 *
 * The host places two copies (mobile after the agent card, desktop in the
 * agent card's sticky wrapper, R30). They share one interest store, so they
 * agree, and `assist_cta_view` is sent once per listing per page load.
 */
import './assist.css';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import AssistWaitlistCard from './AssistWaitlistCard';
import { useAssistMode } from '../../hooks/useAssistMode';
import { fetchMyAssistInterest, registerAssistInterest } from '../../lib/api/assistApi';
import {
  assistCardVariant,
  isAssistEligibleListing,
  type AssistFailure,
  type AssistListingFacts,
} from '../../src/lib/assist/config';
import {
  createAssistInterestStore,
  type AssistInterestStatus,
} from '../../src/lib/assist/interestStore';
import {
  assistJoinReturnPath,
  hasAssistJoinParam,
  withoutAssistParam,
} from '../../src/lib/assist/guestReturn';
import { trackAssistCtaView, trackAssistInterestSubmit } from '../../src/lib/assist/analytics';
import { commonCopy } from '../../src/lib/assist/copy';

const interestStore = createAssistInterestStore({
  getMine: fetchMyAssistInterest,
  register: registerAssistInterest,
});

export interface AssistCardProps {
  /** The listing the card sits on; `propertyType` is the raw column value. */
  listing: AssistListingFacts & { id: string };
  /** The listing's own path (`/property/<slug>`): where a guest returns to. */
  listingPath: string;
  /**
   * The host's sign-in prompt. Receives the path to come back to (the
   * listing with `?assist=join`); the host remembers it the way it remembers
   * any other return (lib/authReturn.ts) and shows its prompt.
   */
  onSignInRequired: (returnTo: string) => void;
  /** The host's signed-in user id, when it has one; re-reads Assist on change. */
  viewerKey?: string | null;
  /**
   * The host's layout classes (`mb-8 lg:hidden`). They go on a plain wrapper,
   * not on the card: `.assist-scope.aq-card` sets `display`, and its two-class
   * selector would beat a one-class Tailwind utility such as `lg:hidden`.
   */
  className?: string;
}

function useInterestStatus(userId: string | null): AssistInterestStatus {
  return useSyncExternalStore(
    interestStore.subscribe,
    () => (userId ? interestStore.status(userId) : 'unknown'),
    () => 'unknown',
  );
}

function failureMessage(failure: AssistFailure): string {
  return failure === 'not_live' ? commonCopy.errors.gateClosed : commonCopy.errors.requestFailed;
}

export default function AssistCard({
  listing,
  listingPath,
  onSignInRequired,
  viewerKey,
  className,
}: AssistCardProps) {
  const assist = useAssistMode(viewerKey);
  const viewerId = assist.viewerId;
  const eligible =
    assist.resolved &&
    assistCardVariant(assist.config) === 'waitlist' &&
    isAssistEligibleListing(listing, assist.config, viewerId);

  const interest = useInterestStatus(eligible ? viewerId : null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<AssistFailure | null>(null);
  const [refused, setRefused] = useState(false);
  const [justJoined, setJustJoined] = useState(false);

  const shown =
    eligible &&
    !refused &&
    interest !== 'unavailable' &&
    !(viewerId && (interest === 'unknown' || interest === 'loading'));

  const join = useCallback(async () => {
    if (!viewerId) {
      onSignInRequired(assistJoinReturnPath(listingPath));
      return;
    }
    setSubmitting(true);
    setFailure(null);
    const outcome = await interestStore.register(viewerId, listing.id);
    setSubmitting(false);
    if (outcome.ok) {
      setJustJoined(true);
      if (outcome.created) trackAssistInterestSubmit();
    } else if (outcome.failure === 'ineligible') {
      // The server knows better (for example a listing whose type is unknown
      // on this page): no card.
      setRefused(true);
    } else {
      setFailure(outcome.failure);
    }
  }, [viewerId, listing.id, listingPath, onSignInRequired]);

  // Signed in: load the waitlist status once per user (shared by every copy).
  useEffect(() => {
    if (eligible && viewerId) interestStore.ensureLoaded(viewerId);
  }, [eligible, viewerId]);

  // Back from sign-in with ?assist=join: consume the parameter, then register.
  useEffect(() => {
    if (!assist.resolved || !viewerId || typeof window === 'undefined') return;
    if (!hasAssistJoinParam(window.location.search)) return;
    const { pathname, search, hash } = window.location;
    window.history.replaceState(window.history.state, '', pathname + withoutAssistParam(search) + hash);
    if (eligible && interestStore.status(viewerId) !== 'joined') void join();
  }, [assist.resolved, viewerId, eligible, join]);

  useEffect(() => {
    if (shown) trackAssistCtaView(listing.id, assist.mode);
  }, [shown, listing.id, assist.mode]);

  if (!shown) return null;

  return (
    <div className={className}>
      <AssistWaitlistCard
        state={interest === 'joined' ? 'joined' : submitting ? 'submitting' : 'idle'}
        onJoin={join}
        errorMessage={failure ? failureMessage(failure) : null}
        animateJoined={justJoined}
      />
    </div>
  );
}
